import React from 'react';
import { Box, CircularProgress } from '@mui/material';
import { usePermissions, useGetIdentity } from 'react-admin';
import { hasPermission } from '../utils/hasPermission';
import { AccessDeniedPage } from './AccessDeniedPage';

interface RequirePermissionProps {
  permission: string;
  children: React.ReactNode;
}

export const RequirePermission = ({ permission, children }: RequirePermissionProps) => {
  const { permissions, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#009A75' }} />
      </Box>
    );
  }

  if (!hasPermission(permissions as string[] | null | undefined, permission)) {
    return <AccessDeniedPage requiredPermission={permission} />;
  }

  return <>{children}</>;
};

interface RequireAnyPermissionProps {
  permissions: string[];
  children: React.ReactNode;
}

export const RequireAnyPermission = ({ permissions, children }: RequireAnyPermissionProps) => {
  const { permissions: userPermissions, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#009A75' }} />
      </Box>
    );
  }

  const list = userPermissions as string[] | null | undefined;
  const granted = permissions.some((p) => hasPermission(list, p));
  if (!granted) {
    return <AccessDeniedPage requiredPermission={permissions.join(' or ')} />;
  }

  return <>{children}</>;
};

export function guard<P extends object>(permission: string, Component: React.ComponentType<P>) {
  const Guarded: React.FC<P> = (props) => (
    <RequirePermission permission={permission}>
      <Component {...props} />
    </RequirePermission>
  );
  Guarded.displayName = `Guarded(${Component.displayName || Component.name || 'Component'})`;
  return Guarded;
}

export function guardAny<P extends object>(permissions: string[], Component: React.ComponentType<P>) {
  const Guarded: React.FC<P> = (props) => (
    <RequireAnyPermission permissions={permissions}>
      <Component {...props} />
    </RequireAnyPermission>
  );
  Guarded.displayName = `GuardedAny(${Component.displayName || Component.name || 'Component'})`;
  return Guarded;
}

// Requires the permission AND that the actor is NOT vendor-scoped.
// Used for pages whose backend list endpoint cannot be vendor-scoped safely
// (e.g. app-transactions, where computing the visible-id set blows the
// PostgREST URL length limit). Vendor-scoped admins can still see equivalent
// data through other endpoints (charging-sessions, active-sessions).
interface RequireNonVendorScopedProps {
  permission: string;
  children: React.ReactNode;
}

export const RequireNonVendorScoped = ({ permission, children }: RequireNonVendorScopedProps) => {
  const { permissions, isLoading: permLoading } = usePermissions();
  const { identity, isLoading: idLoading } = useGetIdentity();

  if (permLoading || idLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#009A75' }} />
      </Box>
    );
  }

  if (!hasPermission(permissions as string[] | null | undefined, permission)) {
    return <AccessDeniedPage requiredPermission={permission} />;
  }

  const vendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
  if (vendorIds.length > 0) {
    return <AccessDeniedPage requiredPermission={permission} />;
  }

  return <>{children}</>;
};

export function guardNonVendorScoped<P extends object>(permission: string, Component: React.ComponentType<P>) {
  const Guarded: React.FC<P> = (props) => (
    <RequireNonVendorScoped permission={permission}>
      <Component {...props} />
    </RequireNonVendorScoped>
  );
  Guarded.displayName = `GuardedNonVendorScoped(${Component.displayName || Component.name || 'Component'})`;
  return Guarded;
}

export function isPlatformActor(permissions: string[] | null | undefined, vendorIds: string[]): boolean {
  if (!permissions) return false;
  if (permissions.includes('*:*:*')) return true;
  return vendorIds.length === 0 && permissions.includes('sites:read:all');
}

export const RequirePlatformOnly = ({ permission, children }: RequireNonVendorScopedProps) => {
  const { permissions, isLoading: permLoading } = usePermissions();
  const { identity, isLoading: idLoading } = useGetIdentity();

  if (permLoading || idLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress sx={{ color: '#009A75' }} />
      </Box>
    );
  }

  const list = permissions as string[] | null | undefined;
  const vendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
  if (!hasPermission(list, permission) || !isPlatformActor(list, vendorIds)) {
    return <AccessDeniedPage requiredPermission={permission} />;
  }

  return <>{children}</>;
};

export function guardPlatformOnly<P extends object>(permission: string, Component: React.ComponentType<P>) {
  const Guarded: React.FC<P> = (props) => (
    <RequirePlatformOnly permission={permission}>
      <Component {...props} />
    </RequirePlatformOnly>
  );
  Guarded.displayName = `GuardedPlatformOnly(${Component.displayName || Component.name || 'Component'})`;
  return Guarded;
}
